import { useState } from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { suggestedReinclusionReasonTranslationMapping } from "@/models/ReinclusionAgentSuggestionModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { ColoredCard } from "@/uiKit/atoms/ColoredCard.tsx";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { TextTooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { RefuseReinclusionSuggestionModal } from "@/uiKit/organisms/PatientDetailNudges/RefuseReinclusionSuggestionModal.tsx";
import { ProposeReinclusionModal } from "@/uiKit/organisms/ProposeReinclusionModal.tsx";
import { toDateTime } from "@/utils/dateUtils.ts";
import { formatNumber } from "@/utils/mathUtils.ts";

type AgentReinclusionSuggestionBannerProps = {
  patientId: string;
  className?: string;
};

export const AgentReinclusionSuggestionBanner = ({ patientId, className }: AgentReinclusionSuggestionBannerProps) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const [isProposeOpen, setIsProposeOpen] = useState(false);
  const [isRefuseOpen, setIsRefuseOpen] = useState(false);

  const suggestion = Queries.practitioner.useReinclusionStateForNurse(patientId).suggestion;
  const acceptMutation = Queries.practitioner.useAcceptReinclusionAgentSuggestion();
  const refuseMutation = Queries.practitioner.useRefuseReinclusionAgentSuggestion();

  if (!suggestion) {
    return null;
  }

  const showTir =
    suggestion.suggestedReason === "tir_below_threshold" || suggestion.suggestedReason === "tir_and_tbr_outside_bounds";
  const showTbr =
    suggestion.suggestedReason === "tbr_above_threshold" || suggestion.suggestedReason === "tir_and_tbr_outside_bounds";
  const daysSinceLastTLS = suggestion.notTelemonitoredSince
    ? formatNumber(-toDateTime(suggestion.notTelemonitoredSince).diffNow("days").days, 0)
    : undefined;

  const isPending = acceptMutation.isPending || refuseMutation.isPending;

  return (
    <>
      <ColoredCard id="agent-reinclusion-banner" square border color="blue" className={cx(styles.banner, className)}>
        <CardHeader
          startIcon={<Icons.aiStars className={styles.icon} />}
          classes={{ title: styles.title }}
          title={t(suggestedReinclusionReasonTranslationMapping(suggestion.suggestedReason))}
          variant="banner"
          action={
            suggestion.autoResolveDate ? (
              <TextTooltip
                id="agent-reinclusion-auto-archive-tag"
                placement="bottom-end"
                text={t("reinclusion.agent.banner.autoArchiveTooltip", {
                  date: toDateTime(suggestion.autoResolveDate).toLocaleString({ day: "numeric", month: "short" }),
                })}
                textClassName={styles.title}
                wrapperClassName={styles.autoArchiveTag}
              >
                <Tag
                  label={t("reinclusion.agent.banner.autoArchiveTag", {
                    date: toDateTime(suggestion.autoResolveDate).toLocaleString({ day: "numeric", month: "short" }),
                  })}
                  icon={<Icons.info className={styles.iconHelp} />}
                  color="neutral"
                />
              </TextTooltip>
            ) : undefined
          }
        />
        <CardContent className={styles.content}>
          <div className={styles.statsRow}>
            {suggestion.tir && showTir && (
              <div className={styles.stats}>
                <Typography variant="body">{t("reinclusion.agent.banner.tir")}</Typography>
                <Tag label={formatNumber(suggestion.tir, 0) + "%"} color="pink" />
              </div>
            )}
            {suggestion.tbr && showTbr && (
              <div className={styles.stats}>
                <Typography variant="body">{t("reinclusion.agent.banner.tbr")}</Typography>
                <Tag label={formatNumber(suggestion.tbr, 0) + "%"} color="pink" />
              </div>
            )}
          </div>
          {daysSinceLastTLS && (
            <Typography>
              {t("reinclusion.agent.banner.notTelemonitoredSince", {
                days: daysSinceLastTLS,
              })}
            </Typography>
          )}
        </CardContent>
        <CardFooter position="start">
          <Button
            id="agent-reinclusion-refuse"
            variant="outlined"
            data-action="inclusion.reinclusion.reject"
            onClick={() => setIsRefuseOpen(true)}
            disabled={isPending}
          >
            {t("reinclusion.agent.banner.refuseCTA")}
          </Button>
          <Button
            id="agent-reinclusion-accept"
            variant="contained"
            data-action="inclusion.reinclusion.accept"
            onClick={() => setIsProposeOpen(true)}
            disabled={isPending}
          >
            {t("reinclusion.agent.banner.acceptCTA")}
          </Button>
        </CardFooter>
      </ColoredCard>
      <ProposeReinclusionModal
        open={isProposeOpen}
        onClose={() => setIsProposeOpen(false)}
        isPending={acceptMutation.isPending}
        onSubmit={nurseMessage =>
          acceptMutation.mutate(
            { patientId, nurseMessage },
            {
              onSuccess: () => {
                setIsProposeOpen(false);
                TextToast.success(t("reinclusion.agent.proposeSuccessToast"));
              },
            },
          )
        }
      />
      <RefuseReinclusionSuggestionModal
        open={isRefuseOpen}
        onClose={() => setIsRefuseOpen(false)}
        isPending={refuseMutation.isPending}
        onSubmit={(reason, reasonFreeText) =>
          refuseMutation.mutate(
            { patientId, reason, reasonFreeText },
            {
              onSuccess: () => {
                setIsRefuseOpen(false);
                TextToast.success(t("reinclusion.agent.refuseSuccessToast"));
              },
            },
          )
        }
      />
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  banner: css({}),
  title: css({
    whiteSpace: "pre-line",
  }),
  autoArchiveTag: css({
    width: "fit-content",
    whiteSpace: "nowrap",
  }),
  content: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    flex: "1 1 auto",
  }),
  icon: css({
    width: FontSizes.h5,
    height: FontSizes.h5,
  }),
  iconHelp: css({
    width: FontSizes.bodySmall,
    height: FontSizes.bodySmall,
  }),
  statsRow: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(8),
  }),
  stats: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(4),
  }),
});
